import { useEffect, useState } from "react";
import { Package } from "lucide-react";
import { cn } from "@cieda/djayma-ui";

export interface ProductImageProps {
  readonly src: string | null | undefined;
  readonly alt: string;
  readonly className?: string;
  readonly size?: "sm" | "md" | "lg";
}

/**
 * Product thumbnail with graceful placeholder fallback.
 * Handles: empty URL → placeholder, valid URL → image, load error → placeholder.
 */
export function ProductImage({ src, alt, className, size = "sm" }: ProductImageProps) {
  const [hasError, setHasError] = useState(false);

  useEffect(() => { setHasError(false); }, [src]);

  const isEmpty = !src || src.trim() === "";
  const showPlaceholder = isEmpty || hasError;
  const iconClass = size === "sm" ? "h-4 w-4" : size === "md" ? "h-6 w-6" : "h-10 w-10";

  if (showPlaceholder) {
    return (
      <div
        className={cn("flex items-center justify-center bg-stone-50 text-stone-300", className)}
        role="img"
        aria-label={alt}
      >
        <Package className={iconClass} aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn("object-cover", className)}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
}
